import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Select, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { selectors } from 'state';
import { getCompletedPlayerAIReportOptions } from '../AI/aiReportShared';

type Props = {
  playerId: number;
};

const { Text } = Typography;

const PlayerAIReportTab: React.FC<Props> = ({ playerId }) => {
  const promptsObj = useSelector(selectors.prompts);
  const responses = useSelector(selectors.llmResponses);
  const promptsWaiting = useSelector(selectors.promptsWaiting);
  const responsesWaiting = useSelector(selectors.llmResponsesWaiting);
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);

  const reportOptions = useMemo(
    () => getCompletedPlayerAIReportOptions(promptsObj, responses, playerId),
    [playerId, promptsObj, responses]
  );

  useEffect(() => {
    if (reportOptions.length === 0) {
      setSelectedPromptId(null);
      return;
    }

    const stillExists =
      selectedPromptId != null &&
      reportOptions.some(option => option.promptId === selectedPromptId);

    if (!stillExists) {
      setSelectedPromptId(reportOptions[0].promptId);
    }
  }, [reportOptions, selectedPromptId]);

  const selectedOption =
    reportOptions.find(option => option.promptId === selectedPromptId) ??
    reportOptions[0];

  if (reportOptions.length === 0) {
    if (promptsWaiting || responsesWaiting) {
      return <Text type="secondary">Loading AI analysis...</Text>;
    }

    return (
      <Text type="secondary">No AI analysis available for this player.</Text>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateRows:
          reportOptions.length > 1 ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {reportOptions.length > 1 && selectedOption && (
        <Select<number>
          size="small"
          style={{ width: 220, marginBottom: 8 }}
          value={selectedOption.promptId}
          onChange={value => setSelectedPromptId(value)}
        >
          {reportOptions.map(option => (
            <Select.Option key={option.promptId} value={option.promptId}>
              {option.promptName}
            </Select.Option>
          ))}
        </Select>
      )}

      {selectedOption && (
        <div
          style={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            padding: 12,
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            background: '#fff',
          }}
        >
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            {selectedOption.promptName}
          </Text>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {selectedOption.response}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default PlayerAIReportTab;
